import React, { useRef, useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import './App.css';
import { AuthProvider } from './auth/AuthContext';
import Login from './auth/Login';
import Register from './auth/Register';
import ProtectedRoute from './auth/ProtectedRoute';
import './auth/auth.css';

// @ts-ignore - Adding type declaration for alawmulaw
declare const alawmulaw: { mulaw: { encode: (data: Int16Array) => Uint8Array; decode: (data: Uint8Array) => Int16Array } };

// Get base URL from environment variables
const BASE_URL = import.meta.env.VITE_BASE_URL || 'https://tacoai.teamta.net';

// Derive other URLs from the base URL
const SERVER_URL = BASE_URL;
const WS_PROTOCOL = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
const WS_URL = `${WS_PROTOCOL}//` + BASE_URL.replace(/(^\w+:|^)\/\//, '') + '/ws';

console.log('Server URL:', SERVER_URL);
console.log('WebSocket URL:', WS_URL);

// AudioWorklet processor code as a string, will be loaded into AudioContext
const workletProcessorCode = `
class VoiceProcessor extends AudioWorkletProcessor {
  constructor() {
    super();
    this._isTalking = false;
    this._silenceCounter = 0;
    this._speechCounter = 0;
    
    // Voice detection parameters
    this._voiceThreshold = 0.015;     // Slightly reduced threshold for easier voice detection
    this._noiseFloor = 0.007;         // Slightly reduced noise floor
    this._adaptationRate = 0.03;      // Slower adaptation to environment changes
    this._thresholdMultiplier = 1.8;  // Slightly reduced multiplier for more sensitivity
    
    // Sentence detection parameters - DRASTICALLY increased for much longer pauses
    this._minSpeechFrames = 6;        // Reduced for quicker start detection
    this._shortPauseDuration = 100;   // DOUBLED from 50 to 100
    this._sentenceEndDuration = 300;  // DOUBLED from 150 to 300 (about 6 seconds of silence)
    this._maxSentenceDuration = 3000; // Increased to 60 seconds max sentence
    
    // State tracking
    this._energyHistory = new Array(15).fill(0);
    this._energyIndex = 0;
    this._adaptiveThreshold = 0.015;
    this._sentenceDurationCounter = 0; // Count frames in current sentence
    this._inSentencePause = false;     // Whether we're in a pause within a sentence
    this._pauseDuration = 0;           // Track pause duration within a sentence
    this._lastSpeechFrame = 0;         // Count of frames since last speech was detected
    this._previousEnergy = 0;          // Track previous energy for smoothing
    
    // Debug mode
    this._debugMode = false;           // Set to true for additional logging
    this._debugFrameCounter = 0;       // Track frames for periodic logging
    this._debugInterval = 100;         // Log every X frames
  }
  
  process(inputs, outputs, parameters) {
    const input = inputs[0];
    if (input.length > 0) {
      const samples = input[0];
      
      // Increment tracking counters
      this._debugFrameCounter++;
      
      // Calculate energy level from samples with emphasis on peaks
      let energy = 0;
      let peakCount = 0;
      
      for (let i = 0; i < samples.length; i++) {
        const sampleAbs = Math.abs(samples[i]);
        energy += sampleAbs;
        
        // Count peaks above certain threshold - human voice has more peaks
        if (sampleAbs > 0.1) {
          peakCount++;
        }
      }
      
      energy = energy / samples.length;
      
      // Apply smoothing to energy levels to reduce fluctuations
      energy = energy * 0.7 + this._previousEnergy * 0.3;
      this._previousEnergy = energy;
      
      const peakRatio = peakCount / samples.length;
      
      // Update energy history and calculate average
      this._energyHistory[this._energyIndex] = energy;
      this._energyIndex = (this._energyIndex + 1) % this._energyHistory.length;
      
      // Calculate average energy (excluding outliers for stability)
      const sortedEnergies = [...this._energyHistory].sort((a, b) => a - b);
      const averageEnergy = sortedEnergies.slice(2, -2).reduce((sum, e) => sum + e, 0) / 
                           (sortedEnergies.length - 4 || 1);
      
      // Adapt threshold based on average energy, but keep minimum sensitivity
      this._adaptiveThreshold = (1 - this._adaptationRate) * this._adaptiveThreshold + 
                              this._adaptationRate * Math.max(
                                this._noiseFloor,
                                averageEnergy * this._thresholdMultiplier
                              );
      
      // Determine if current frame contains speech
      const isSpeechFrame = energy > this._adaptiveThreshold && peakRatio > 0.01;
      
      // Debug logging
      if (this._debugMode && (this._debugFrameCounter % this._debugInterval === 0)) {
        this.port.postMessage({
          event: 'debug_log',
          data: {
            frame: this._debugFrameCounter,
            energy: energy,
            threshold: this._adaptiveThreshold,
            isSpeech: isSpeechFrame,
            isTalking: this._isTalking,
            silenceCounter: this._silenceCounter,
            inPause: this._inSentencePause,
            pauseDuration: this._pauseDuration,
            lastSpeechFrame: this._lastSpeechFrame
          }
        });
      }
      
      // STATE MACHINE FOR SENTENCE DETECTION
      
      // CASE 1: Not currently in a speech segment
      if (!this._isTalking) {
        if (isSpeechFrame) {
          this._speechCounter++;
          this._silenceCounter = 0;
          
          // If we have enough consecutive speech frames, start a new sentence
          if (this._speechCounter >= this._minSpeechFrames) {
            this._isTalking = true;
            this._sentenceDurationCounter = 0;
            this._inSentencePause = false;
            this._pauseDuration = 0;
            this._lastSpeechFrame = 0;
            
            // Notify that speech has started
            this.port.postMessage({
              event: 'speech_start',
              energy: energy,
              threshold: this._adaptiveThreshold,
              peakRatio: peakRatio
            });
          }
        } else {
          // Reset speech counter when no speech is detected
          this._speechCounter = 0;
        }
      }
      // CASE 2: Currently in a speech segment
      else {
        this._sentenceDurationCounter++;
        
        // If there's speech in this frame
        if (isSpeechFrame) {
          // Reset the last speech frame counter
          this._lastSpeechFrame = 0;
          
          // If we were in a pause, but speech resumed before sentence end threshold
          if (this._inSentencePause) {
            // Resume the sentence - we were just in a pause between words
            this._inSentencePause = false;
            this._pauseDuration = 0;
          }
          
          this._silenceCounter = 0;
        }
        // If there's no speech in this frame
        else {
          this._silenceCounter++;
          this._lastSpeechFrame++; // Increment frames since last speech
          
          // Handle pauses within sentences
          if (!this._inSentencePause && this._silenceCounter > this._shortPauseDuration) {
            // We've detected a pause that's longer than typical inter-word pause
            this._inSentencePause = true;
            this._pauseDuration = 0;
          }
          
          if (this._inSentencePause) {
            this._pauseDuration++;
            
            // Send pause duration updates for visualization every 30 frames
            if (this._pauseDuration % 30 === 0) {
              this.port.postMessage({
                event: 'pause_update',
                pauseDuration: this._pauseDuration,
                sentenceDuration: this._sentenceDurationCounter,
                energy: energy,
                threshold: this._adaptiveThreshold
              });
            }
          }
          
          // End the sentence if:
          // 1. We've been in a pause long enough to indicate end of sentence
          // 2. OR we've reached the maximum allowed sentence duration
          // 3. OR it's been more than 300 frames (6 seconds) since the last speech was detected
          const isEndOfSentence = 
            (this._inSentencePause && this._pauseDuration > this._sentenceEndDuration) ||
            (this._sentenceDurationCounter > this._maxSentenceDuration) ||
            (this._lastSpeechFrame > 300); // 6 seconds of silence
            
          if (isEndOfSentence) {
            let reasonForEnd = 'silence_threshold_exceeded';
            if (this._sentenceDurationCounter > this._maxSentenceDuration) {
              reasonForEnd = 'max_duration_exceeded';
            } else if (this._lastSpeechFrame > 300) {
              reasonForEnd = 'timeout_exceeded';
            }
            
            this._isTalking = false;
            this._speechCounter = 0;
            this._silenceCounter = 0;
            this._inSentencePause = false;
            
            // Notify that sentence has ended
            this.port.postMessage({
              event: 'speech_end',
              energy: energy,
              threshold: this._adaptiveThreshold,
              peakRatio: peakRatio,
              sentenceDuration: this._sentenceDurationCounter,
              reasonForEnd: reasonForEnd
            });
          }
        }
      }
      
      // Always send audio data to main thread
      this.port.postMessage({
        event: 'audio_data',
        data: Array.from(samples),
        isTalking: this._isTalking,
        energy: energy,
        threshold: this._adaptiveThreshold,
        peakRatio: peakRatio,
        inSentence: this._isTalking,
        inPause: this._isTalking && this._inSentencePause,
        pauseDuration: this._pauseDuration,
        sentenceDuration: this._sentenceDurationCounter
      });
    }
    
    return true;
  }
}

registerProcessor('voice-processor', VoiceProcessor);
`;

function MainApp() {
  const [started, setStarted] = useState(false);
  const [audioActive, setAudioActive] = useState(false);
  const [status, setStatus] = useState('');
  const [isTalking, setIsTalking] = useState(false);
  const [isAssistantSpeaking, setIsAssistantSpeaking] = useState(false);
  const [isWaitingForResponse, setIsWaitingForResponse] = useState(false);
  
  // Add VAD debug visualization states
  const [audioEnergy, setAudioEnergy] = useState(0);
  const [vadThreshold, setVadThreshold] = useState(0.01);
  const [showVadDebug, setShowVadDebug] = useState(false);
  
  // Add settings panel state
  const [vadSettings, setVadSettings] = useState({
    voiceThreshold: 0.015,
    noiseFloor: 0.007,
    adaptationRate: 0.03,
    minSpeechFrames: 6,
    shortPauseDuration: 100,
    sentenceEndDuration: 300
  });
  
  // Add connection state management
  const [connectionState, setConnectionState] = useState<'disconnected' | 'connecting' | 'connected' | 'reconnecting' | 'error'>('disconnected');
  const reconnectAttemptsRef = useRef<number>(0);
  const maxReconnectAttempts = 5;
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const reconnectIntervalRef = useRef<number>(1000); // Starting with 1 second
  
  // WebSocket and audio processing refs
  const webSocketRef = useRef<WebSocket | null>(null);
  const localStream = useRef<MediaStream | null>(null);
  const videoElement = useRef<HTMLVideoElement | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const outputContextRef = useRef<AudioContext | null>(null);
  const sourceNodeRef = useRef<MediaStreamAudioSourceNode | null>(null);
  const workletNodeRef = useRef<AudioWorkletNode | null>(null);
  const audioQueueRef = useRef<AudioBuffer[]>([]);
  const isPlayingRef = useRef<boolean>(false);
  const snapIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const isRecordingRef = useRef<boolean>(false);
  const audioBufferRef = useRef<Float32Array[]>([]);
  const currentSourceRef = useRef<AudioBufferSourceNode | null>(null);
  const isAssistantSpeakingRef = useRef<boolean>(false); // Add ref to track assistant speaking state
  
  const navigate = useNavigate();
  
  // Helper to convert audio data to mulaw format
  const convertToMulaw = (audioData: Float32Array): Uint8Array => {
    const int16Data = new Int16Array(audioData.length);
    for (let i = 0; i < audioData.length; i++) {
      int16Data[i] = Math.floor(audioData[i] * 32768);
    }
    return alawmulaw.mulaw.encode(int16Data);
  };

  // Helper to play received audio
  const playNextBuffer = () => {
    if (audioQueueRef.current.length === 0) {
      isPlayingRef.current = false;
      
      // When there are no more audio buffers to play, the assistant is done speaking
      setIsAssistantSpeaking(false);
      isAssistantSpeakingRef.current = false; // Update ref to match state
      
      return;
    }
    
    if (!outputContextRef.current) return;
    
    isPlayingRef.current = true;
    const bufferSource = outputContextRef.current.createBufferSource();
    const buffer = audioQueueRef.current.shift();
    if (buffer) {
      bufferSource.buffer = buffer;
    } else {
      return; // No buffer available to play
    }
    bufferSource.connect(outputContextRef.current.destination);
    bufferSource.onended = playNextBuffer;
    bufferSource.start();
    
    // Store reference to the current source node
    currentSourceRef.current = bufferSource;
  };

 // Helper to take photo and send to server
 const takeAndSendPhoto = async () => {
  if (!videoElement.current || !videoElement.current.srcObject) return;

  const video = videoElement.current;
  if (video.readyState < 2) return; // Not enough data

  const canvas = document.createElement('canvas');
  canvas.width = video.videoWidth || 640;
  canvas.height = video.videoHeight || 480;
  const ctx = canvas.getContext('2d');
  if (!ctx) return;

  ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

  canvas.toBlob(async (blob) => {
      if (!blob) return;

      const formData = new FormData();
      formData.append('snap', blob, 'photo.png');

      try {
          // Get the authentication token from localStorage
          const token = localStorage.getItem('token');
          
          if (!token) {
              console.error('No authentication token found for photo upload');
              setStatus('Authentication required. Please log in again.');
              navigate('/login');
              return;
          }
          
          const response = await fetch(`${SERVER_URL}/save_photo`, {
              method: 'POST',
              headers: {
                  'Authorization': `Bearer ${token}` // Add authentication token
              },
              body: formData,
          });

          if (response.ok) {
              console.log('Photo sent successfully');
          } else {
              console.error('Failed to send photo:', response.statusText);
              if (response.status === 401) {
                  console.error('Authentication failed for photo upload');
                  setStatus('Authentication failed. Please log in again.');
                  navigate('/login');
              }
          }
      } catch (err) {
          console.error('Error sending photo:', err);
      }
  }, 'image/png', 0.9);
};

  // Send audio chunk to server
  const sendAudioChunkToServer = (audioData: Float32Array) => {
    if (!webSocketRef.current || webSocketRef.current.readyState !== WebSocket.OPEN) {
      console.log('WebSocket not connected, cannot send audio chunk');
      return;
    }
    
    try {
      // Convert Float32Array to Int16Array for mulaw encoding
      const int16Data = new Int16Array(audioData.length);
      for (let i = 0; i < audioData.length; i++) {
        int16Data[i] = Math.floor(audioData[i] * 32768);
      }
      
      // Encode with mulaw
      const mulawData = alawmulaw.mulaw.encode(int16Data);
      
      // Convert to base64 using chunking to avoid call stack size exceeded
      let base64Audio = '';
      const CHUNK_SIZE = 8192;  // Process in chunks to avoid stack overflow
      
      for (let i = 0; i < mulawData.length; i += CHUNK_SIZE) {
        const chunk = mulawData.slice(i, i + CHUNK_SIZE);
        base64Audio += btoa(
          Array.from(chunk)
            .map(byte => String.fromCharCode(byte))
            .join('')
        );
      }
      
      // Send to server
      webSocketRef.current.send(JSON.stringify({
        event: "audio_chunk",
        media: { 
          payload: base64Audio,
          sampleRate: 24000 // Sample rate for audio processing
        },
        client_datetime: new Date().toLocaleString('en-GB', { hour12: true }).replace(',', '')
      }));

    } catch (err) {
      console.error('Error sending audio chunk to server:', err);
    }
  };

  // Function to decode and queue received audio
  const handleAudioResponse = async (audioBase64: string, sampleRate: number) => {
    if (!outputContextRef.current) {
      outputContextRef.current = new AudioContext({
        sampleRate: sampleRate // Use the sample rate from the server
      });
    }
    
    try {
      // Decode base64 to binary data
      const binaryString = atob(audioBase64);
      const bytes = new Uint8Array(binaryString.length);
      for (let i = 0; i < binaryString.length; i++) {
        bytes[i] = binaryString.charCodeAt(i);
      }
      
      // Create audio buffer from raw audio data
      const audioBuffer = await outputContextRef.current.decodeAudioData(bytes.buffer);
      
      // Add to queue
      audioQueueRef.current.push(audioBuffer);
      
      // Start playback if not already playing
      if (!isPlayingRef.current) {
        playNextBuffer();
      }
      
    } catch (error) {
      console.error('Error processing audio data:', error);
      
      // Fallback method if decodeAudioData fails (for WAV format)
      try {
        // For WAV format, manually create an audio buffer
        const audioContext = outputContextRef.current;
        if (!audioContext) return;
        
        // Get bytes from the current scope instead of outer scope
        const binaryString = atob(audioBase64);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        
        // Skip WAV header (44 bytes) if it's a WAV file
        const headerCheck = new Uint8Array(bytes.buffer.slice(0, 4));
        const isWav = String.fromCharCode(...headerCheck) === 'RIFF';
        
        const audioData = isWav ? new Float32Array(bytes.buffer.slice(44)) : new Float32Array(bytes.length);
        
        // If it's not identified as WAV, convert the bytes to Float32Array
        if (!isWav) {
          for (let i = 0; i < bytes.length; i++) {
            // Convert 8-bit unsigned to float32 (-1.0 to 1.0)
            audioData[i] = (bytes[i] - 128) / 128.0;
          }
        }
        
        // Create a buffer with the correct sample rate
        const audioBuffer = audioContext.createBuffer(
          1, // mono
          audioData.length,
          sampleRate
        );
        
        // Fill the buffer with our audio data
        audioBuffer.getChannelData(0).set(audioData);
        
        // Add to queue and play
        audioQueueRef.current.push(audioBuffer);
        if (!isPlayingRef.current) {
          playNextBuffer();
        }
        
      } catch (fallbackError) {
        console.error('Fallback audio processing also failed:', fallbackError);
      }
    }
  };

  // Enhanced WebSocket message handler with error recovery
  const handleWebSocketMessage = (event: MessageEvent) => {
    try {
      const message = JSON.parse(event.data);
      
      // Reset reconnect attempts on successful message
      reconnectAttemptsRef.current = 0;
      reconnectIntervalRef.current = 1000; // Reset to base interval
      
      if (message.event === 'capture_photo') {
        // Handle interrupt event
        console.log('Interrupt event received:', message.task);
          takeAndSendPhoto();
          console.log("photo captured");
      }

      if (message.event === 'audio_response') {

        const { audio, sampleRate,task_type } = message.data;
        // }

        // Server responded, no longer waiting
        setIsWaitingForResponse(false);
        
        // Set isAssistantSpeaking to true when receiving audio
        setIsAssistantSpeaking(true);
        isAssistantSpeakingRef.current = true; // Update ref value to maintain consistent state
        
        handleAudioResponse(audio, parseInt(sampleRate, 10));
      } else {
        // Handle other message types
        console.log('Received message:', message);
      }
    } catch (error) {
      console.error('Error parsing WebSocket message:', error);
    }
  };

  // Initialize the AudioContext and AudioWorklet
  const initializeAudioContext = async () => {
    try {
      console.log('Initializing Audio Context and Voice Processing...');
      
      // Create AudioContext with specified sample rate
      const audioContext = new AudioContext({ sampleRate: 24000 });
      audioContextRef.current = audioContext;
      
      // Create a blob URL from the worklet processor code
      const blob = new Blob([workletProcessorCode], { type: 'application/javascript' });
      const workletUrl = URL.createObjectURL(blob);
      
      // Add the audio worklet module
      await audioContext.audioWorklet.addModule(workletUrl);
      console.log('Audio worklet module added');
      
      // Clean up the blob URL
      URL.revokeObjectURL(workletUrl);
      
      console.log('Audio processing initialized successfully');
      return true;
    } catch (err) {
      console.error('Error initializing audio processing:', err);
      setStatus('Error initializing audio processing');
      return false;
    }
  };

  // Start audio processing with AudioWorklet
  const startAudioProcessing = async (stream: MediaStream) => {
    if (!audioContextRef.current) {
      console.error('AudioContext not initialized');
      return;
    }
    
    try {
      // Create source node from the media stream
      const sourceNode = audioContextRef.current.createMediaStreamSource(stream);
      sourceNodeRef.current = sourceNode;
      
      // Create AudioWorkletNode
      const workletNode = new AudioWorkletNode(audioContextRef.current, 'voice-processor');
      workletNodeRef.current = workletNode;
      
      // Connect the nodes
      sourceNode.connect(workletNode);
      
      // Don't connect to destination to avoid feedback
      // workletNode.connect(audioContextRef.current.destination);
      
      // Listen for messages from the processor
      workletNode.port.onmessage = (event) => {
        const { data } = event;
        
        if (data.event === 'speech_start') {

          setIsTalking(true);
          isRecordingRef.current = true;
          audioBufferRef.current = []; // Reset buffer at start of speech
          
          // Update VAD visualization
          setAudioEnergy(data.energy);
          setVadThreshold(data.threshold);
          // If assistant is speaking when user starts talking, stop the assistant's speech
          if (isPlayingRef.current && isAssistantSpeakingRef.current) {
            // Stop current audio source if it exists
            if (currentSourceRef.current) {
              currentSourceRef.current.onended = null; // Remove the callback
              currentSourceRef.current.stop();
              currentSourceRef.current = null;
            }
            
            // Clear audio queue to prevent further playback
            audioQueueRef.current = [];
            isPlayingRef.current = false;
            setIsAssistantSpeaking(false);
            isAssistantSpeakingRef.current = false; // Update ref to match state
            
          }
          
          // Send an event to the server that speech started
          if (webSocketRef.current && webSocketRef.current.readyState === WebSocket.OPEN) {
            webSocketRef.current.send(JSON.stringify({
              event: "log",
              data: "started_talking"
            }));
          }
        }
        else if (data.event === 'speech_end') {

          setIsTalking(false);
          isRecordingRef.current = false;
          
          // Update VAD visualization
          setAudioEnergy(data.energy);
          setVadThreshold(data.threshold);
          
          // Send combined audio buffer if we have collected any
          if (audioBufferRef.current.length > 0) {
            // Combine all chunks into one array
            const combinedLength = audioBufferRef.current.reduce((acc, chunk) => acc + chunk.length, 0);
            const combinedBuffer = new Float32Array(combinedLength);
            
            let offset = 0;
            for (const chunk of audioBufferRef.current) {
              combinedBuffer.set(chunk, offset);
              offset += chunk.length;
            }
            
            // Send the complete speech segment
            sendAudioChunkToServer(combinedBuffer);
                        
            // Clear the buffer
            audioBufferRef.current = [];
          }
          
          // Send an event to the server that speech ended
          if (webSocketRef.current && webSocketRef.current.readyState === WebSocket.OPEN) {
            webSocketRef.current.send(JSON.stringify({
              event: "log",
              data: "end_speech"
            }));
            
            // Also set waiting state when sending the end_speech event
            setIsWaitingForResponse(true);
          }
        }
        else if (data.event === 'audio_data') {
          // Process incoming audio data
          const audioData = new Float32Array(data.data);
          
          // Update VAD visualization (do this less frequently to avoid performance issues)
          if (Math.random() < 0.2) { // Only update ~20% of the time to reduce UI updates
            setAudioEnergy(data.energy);
            setVadThreshold(data.threshold);
          }
                    
          // If currently talking, collect the audio data
          if (data.isTalking && isRecordingRef.current) {
            audioBufferRef.current.push(audioData);
            
            // Option: Send real-time chunks during speech
            sendAudioChunkToServer(audioData);
          }
        }
      };
      
    } catch (err) {
      console.error('Error starting audio processing:', err);
    }
  };

  // Start WebSocket connection and audio processing
  const startVoiceChat = async () => {
    try {
      setStarted(true);
      setStatus('Initializing...');
      setConnectionState('connecting');

      // Initialize AudioContext first
      const initialized = await initializeAudioContext();
      if (!initialized) {
        throw new Error('Failed to initialize audio context');
      }

      // Get media stream with audio and video
      console.log('Requesting media permissions...');
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          sampleRate: 24000
        },
        video: true
      });
      console.log('Media permissions granted:', stream.getTracks().map(t => `${t.kind}: ${t.label}`));
      
      localStream.current = stream;

      // Connect media stream to video element
      if (videoElement.current) {
        videoElement.current.srcObject = stream;
        console.log('Video element connected to media stream');
      }
      
      // Get the authentication token from localStorage
      const token = localStorage.getItem('token');
      if (!token) {
        console.error('No authentication token found');
        setStatus('Authentication required. Please log in again.');
        navigate('/login');
        return;
      }
      
      // Use the simplified WebSocket URL with token in query parameter
      const wsUrl = `${WS_URL}?token=${token}`;
      console.log('Attempting authenticated WebSocket connection');
      setStatus('Connecting to server...');
      
      // Create the WebSocket connection
      const webSocket = new WebSocket(wsUrl);
      webSocketRef.current = webSocket;

      // Set connection timeout
      const connectionTimeout = setTimeout(() => {
        if (webSocket.readyState !== WebSocket.OPEN) {
          console.error('WebSocket connection timeout');
          setStatus('Connection timeout. Server might be down or unreachable.');
          webSocket.close();
          
          // Try to reconnect
          reconnectWebSocket();
        }
      }, 5000); // 5 second timeout

      webSocket.onopen = () => {
        // Clear the connection timeout
        clearTimeout(connectionTimeout);
        
        console.log('WebSocket connection opened successfully');
        setStatus('Connection established! Initializing audio processing...');
        setConnectionState('connected');
        
        // Reset reconnection attempts on successful connection
        reconnectAttemptsRef.current = 0;
        reconnectIntervalRef.current = 1000; // Reset to base interval
        
        // Send initial VAD settings to server
        webSocket.send(JSON.stringify({
          event: "vad_settings",
          settings: vadSettings
        }));
        
        // Start audio processing
        startAudioProcessing(stream).then(() => {
          setStatus('Voice detection active');
          setAudioActive(true);
        });
      };

      webSocket.onmessage = handleWebSocketMessage;

      webSocket.onclose = (event) => {
        clearTimeout(connectionTimeout);
        console.log('WebSocket connection closed:', event.code, event.reason);
        
        if (event.code === 1000) {
          // Normal closure
          setStatus('Connection closed normally');
          setConnectionState('disconnected');
        } else {
          // Abnormal closure
          setStatus(`Connection lost (${event.code}): ${event.reason || 'No reason provided'}`);
          
          // Only attempt reconnection if we were previously connected and component is still mounted
          if (started && connectionState === 'connected') {
            reconnectWebSocket();
          } else {
            setConnectionState('disconnected');
          }
        }
        
        setAudioActive(false);
      };

      webSocket.onerror = (error) => {
        clearTimeout(connectionTimeout);
        console.error('WebSocket error occurred:', error);
        setStatus('Connection error. Check if server is running.');
        setConnectionState('error');
        
        // Don't call stopVoiceChat here to avoid double cleanup
        // The onclose handler will be called after this and handle reconnection
      };
      
      // Update current VAD settings when voice processing is active
      if (workletNodeRef.current) {
        workletNodeRef.current.port.postMessage({
          event: 'update_settings',
          settings: vadSettings
        });
      }
    } catch (err) {
      console.error('Error starting voice chat:', err);
      setStatus(`Error: ${err instanceof Error ? err.message : String(err)}`);
      setConnectionState('error');
      
      // Try to reconnect if it's a connection issue
      if (String(err).includes('connection') || String(err).includes('network')) {
        reconnectWebSocket();
      } else {
        setStarted(false);
        setAudioActive(false);
      }
    }
  };

  // Update VAD settings 
  const updateVadSettings = (newSettings: Partial<typeof vadSettings>) => {
    const updatedSettings = {
      ...vadSettings,
      ...newSettings
    };
    
    setVadSettings(updatedSettings);
    
    // Send updated settings to the AudioWorklet
    if (workletNodeRef.current) {
      workletNodeRef.current.port.postMessage({
        event: 'update_settings',
        settings: updatedSettings
      });
    }
    
  };

  // Enhanced reconnection with exponential backoff
  const reconnectWebSocket = () => {
    if (reconnectAttemptsRef.current >= maxReconnectAttempts) {
      console.log('Maximum reconnection attempts reached');
      setStatus('Connection failed after multiple attempts. Please try again later.');
      setConnectionState('error');
      return;
    }
    
    // Clear any existing timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
    }
    
    setStatus(`Reconnecting... Attempt ${reconnectAttemptsRef.current + 1}/${maxReconnectAttempts}`);
    setConnectionState('reconnecting');
    
    // Implement exponential backoff
    const delay = Math.min(30000, reconnectIntervalRef.current * Math.pow(1.5, reconnectAttemptsRef.current));
    console.log(`Reconnecting in ${delay}ms`);
    
    reconnectTimeoutRef.current = setTimeout(() => {
      reconnectAttemptsRef.current++;
      reconnectIntervalRef.current = delay;
      
      // Close existing connection if any
      if (webSocketRef.current) {
        webSocketRef.current.close();
        webSocketRef.current = null;
      }
      
      // Re-initialize connection
      startVoiceChat().catch(err => {
        console.error('Reconnection failed:', err);
        setStatus(`Reconnection failed: ${err instanceof Error ? err.message : String(err)}`);
        // Try again with backoff
        reconnectWebSocket();
      });
    }, delay);
  };

  // Stop WebSocket connection with enhanced cleanup
  const stopVoiceChat = () => {
    console.log('Stopping voice chat...');
    
    // Clear any reconnection timeout
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    
    setStarted(false);
    setAudioActive(false);
    setIsTalking(false);
    isRecordingRef.current = false;
    setConnectionState('disconnected');
    
    // Disconnect audio processing nodes
    try {
      if (sourceNodeRef.current) {
        sourceNodeRef.current.disconnect();
        sourceNodeRef.current = null;
      }
      
      if (workletNodeRef.current) {
        workletNodeRef.current.disconnect();
        workletNodeRef.current = null;
      }
      
      // Close AudioContext
      if (audioContextRef.current) {
        audioContextRef.current.close().then(() => {
          console.log('AudioContext closed successfully');
        }).catch(err => {
          console.error('Error closing AudioContext:', err);
        });
        audioContextRef.current = null;
      }
      
      // Close output AudioContext
      if (outputContextRef.current) {
        outputContextRef.current.close().then(() => {
          console.log('Output AudioContext closed successfully');
        }).catch(err => {
          console.error('Error closing output AudioContext:', err);
        });
        outputContextRef.current = null;
      }
    } catch (err) {
      console.error('Error stopping audio processing:', err);
    }
    
    // Close WebSocket with proper shutdown sequence
    if (webSocketRef.current) {
      if (webSocketRef.current.readyState === WebSocket.OPEN) {
        console.log('Sending stop event to server');
        try {
          webSocketRef.current.send(JSON.stringify({ event: "stop" }));
        } catch (err) {
          console.error('Error sending stop event:', err);
        }
      }
      
      // Give server a moment to process the stop event before closing
      setTimeout(() => {
        if (webSocketRef.current) {
          webSocketRef.current.close();
          webSocketRef.current = null;
          console.log('WebSocket connection closed');
        }
      }, 300);
    }
    
    // Stop all media tracks
    if (localStream.current) {
      localStream.current.getTracks().forEach(track => {
        track.stop();
        console.log(`Stopped ${track.kind} track: ${track.label}`);
      });
      localStream.current = null;
    }
    
    // Clear video element
    if (videoElement.current) {
      videoElement.current.srcObject = null;
    }
    
    // Clear any intervals
    if (snapIntervalRef.current) {
      clearInterval(snapIntervalRef.current);
      snapIntervalRef.current = null;
    }
    
    // Clear audio queue
    audioQueueRef.current = [];
    
    setStatus('Disconnected');
  };

  // Cleanup on unmount
  useEffect(() => {
    console.log('Component mounted');
    
    // Check if alawmulaw library is available
    if (typeof alawmulaw === 'undefined') {
      console.error('alawmulaw library not loaded! Make sure to include the script tag in your HTML.');
    } else {
      console.log('alawmulaw library detected and available');
    }
    
    return () => {
      console.log('Component unmounting, cleaning up resources');
      stopVoiceChat();
    };
  }, []);

  // New function to handle logout
  const handleLogout = () => {
    // Clean up voice chat resources first
    if (started) {
      stopVoiceChat();
    }
    
    // Navigate to login page
    navigate('/login');
  };

  return (
    <div className="app-container">
      {/* Add logout button in header */}
      <div className="app-header">
        <button className="logout-btn" onClick={handleLogout}>
          Logout
        </button>
      </div>
      
      {!started && (
        <button className="webrtc-btn" onClick={startVoiceChat}>
          Talk to Me
        </button>
      )}
      
      {/* Hidden video for snapshots (must be rendered) */}
      <video
          ref={videoElement}
          width={640}
          height={480}
          style={{ 
            opacity: 0,
            position: 'absolute',
            pointerEvents: 'none',
            zIndex: -1
          }}
          autoPlay
          playsInline
          muted
        />
      
      {/* Mic animation when audio is active */}
      {started && (
        <div className={`mic${isTalking ? ' talking' : ''}${isAssistantSpeaking ? ' assistant-speaking' : ''}${isWaitingForResponse ? ' waiting' : ''}`}>
          <i className="mic-icon"></i>
          <div className="mic-shadow"></div>
        </div>
      )}

      {/* Bye button with improved positioning */}
      {started && (
        <div style={{ position: 'absolute', bottom: '20px', width: '100%', display: 'flex', justifyContent: 'center', zIndex: 10 }}>
          <button className="webrtc-btn" onClick={stopVoiceChat}>
            Bye!
          </button>
        </div>
      )}
      
      {/* Manual reconnect button when in error state */}
      {connectionState === 'error' && started && (
        <button 
          style={{ 
            position: 'absolute',
            left: '50%',
            bottom: 80,
            transform: 'translateX(-50%)',
            background: '#2196F3',
            color: 'white',
            border: 'none',
            borderRadius: 20,
            padding: '8px 15px',
            cursor: 'pointer'
          }}
          onClick={() => {
            reconnectAttemptsRef.current = 0;
            reconnectIntervalRef.current = 1000;
            reconnectWebSocket();
          }}
        >
          Try Reconnecting
        </button>
      )}
    </div>
  );
}

function App() {
  return (
    <Router>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="/register" element={<Register />} />
          <Route 
            path="/" 
            element={
              <ProtectedRoute>
                <MainApp />
              </ProtectedRoute>
            } 
          />
          <Route path="*" element={<Navigate to="/" />} />
        </Routes>
      </AuthProvider>
    </Router>
  );
}

export default App;
